import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  TypePolicies,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ErrorResponse, onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";

import { reportError, TransformBreadcrumbFn } from "../modules/error";
import { ApolloGraphQLResponseContext } from "../types/apollo-response-context";

export const enhanceFetchBreadcrumbs: TransformBreadcrumbFn = (
  breadcrumb,
  hint
) => {
  if (
    breadcrumb.category === "fetch" &&
    breadcrumb?.data?.url?.endsWith("/graphql")
  ) {
    let graphQLOperationName: string | undefined = undefined;

    try {
      graphQLOperationName = JSON.parse(hint?.input?.[1]?.body)?.operationName;
    } catch (error) {
      // it's fine if we can't parse the request body
    }

    return {
      ...breadcrumb,
      data: {
        ...(breadcrumb.data ?? {}),
        apiRequestId: hint?.response?.headers?.get("x-request-id"),
        graphQLOperationName,
      },
    };
  }

  return breadcrumb;
};

export function createApolloClient(params: {
  graphQLURI: string;
  appNameHeaderValue: string;
  typePolicies?: TypePolicies;
  disableAuth?: boolean;
  onError?(error: ErrorResponse): void;
  isAuthenticated?(): Promise<boolean> | boolean;
  getAccessToken?(): Promise<string> | string;
  initialCacheState?: NormalizedCacheObject;
  getHeaders?(): Record<string, string>;
}) {
  /**
   * Enhances the response with requestId populated form the response header
   */
  const requestIdLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context: ApolloGraphQLResponseContext = {
        requestId: (operation.getContext().response.headers as Headers).get(
          "x-request-id"
        )!,
      };

      response.context = {
        ...(response.context ?? {}),
        ...context,
      };

      return response;
    });
  });

  const uploadableHttpLink = createUploadLink({
    uri: params.graphQLURI,
  });

  const authLink = setContext(async (_, { headers: prevHeaders }) => {
    const headers = {
      ...prevHeaders,
      ...(params.getHeaders?.() ?? {}),
      "x-app-name": params.appNameHeaderValue,
      // TODO add version & device info context
    };

    if (await params.isAuthenticated?.()) {
      const accessToken = await params.getAccessToken?.();

      if (accessToken) {
        headers["id-token"] = `Bearer ${accessToken}`;
      } else {
        reportError(
          new Error("Could not get access token while supposedly authenticated")
        );
      }
    }

    return {
      headers,
    };
  });

  const cache = new InMemoryCache({
    typePolicies: params.typePolicies,
  });

  if (params.initialCacheState) {
    cache.restore(params.initialCacheState);
  }

  return new ApolloClient({
    link: requestIdLink.concat(
      params.disableAuth
        ? uploadableHttpLink
        : authLink
            .concat(
              onError((error) => {
                params.onError?.(error);
              })
            )
            .concat(uploadableHttpLink)
    ),
    cache,
    defaultOptions: {
      watchQuery: {
        // by default, `useQuery` will return cached data (if any),
        // but will make a request and refresh with any new data.
        // this is the 'stale-while-revalidate' pattern:
        // https://web.dev/stale-while-revalidate/
        fetchPolicy: "cache-and-network",
      },
      query: {
        fetchPolicy: "network-only",
      },
    },
  });
}
